import { graphHooks } from '@tunasong/graph-lib/react'
import { capitalize } from '@tunasong/models'
import { useLayoutsExtended } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import { AppChrome, EntityChrome, ErrorMessage, usePageMetadata } from '@tunasong/tunasong-ui'
import { FullPageProgress, useCurrentLayoutName, useParams } from '@tunasong/ui-lib'
import { useEffect, useEffectEvent } from 'react'

/** Router wrapper for the AppChrome */
export const RouterAppChrome = () => {
  const { entityId } = useParams()

  const layoutName = useCurrentLayoutName()

  const { entity, error, isLoading, isFetching } = graphHooks.useEntity(entityId)
  const layouts = useLayoutsExtended(entity)

  const layout = layouts.find(layout => layout.name === layoutName) ?? layouts[0]

  const showContainer = layout?.container !== false
  const container = layout?.container ? layout.container : {}
  const showHeader = showContainer && container?.showHeader !== false
  const showNavigationBar = layout?.showNavigationBar
  const showSidebar = container?.showSidebar !== false

  // Set the page title and icon name
  const { setName, setDescription } = usePageMetadata()
  const pageEvent = useEffectEvent((entity?: Persisted<Entity>) => {
    if (!entity) {
      return
    }
    const name = entity.name ? `${capitalize(entity.type)} : ${entity.name ?? 'Untitled'}` : entity.type
    setName(name)
    if (entity.description) {
      setDescription(entity.description)
    }
  })

  useEffect(() => {
    pageEvent(entity)
  }, [entity])

  // We accept error (404) iff the entity is found (i.e., it is in the cache, newly created
  const isError = Boolean(error) && !entity
  const isBusy = Boolean(!entity && (isLoading || isFetching))

  return (
    <AppChrome
      layouts={layouts}
      entity={entity}
      currentLayout={layoutName}
      showNavigationBar={showNavigationBar}
      showPageHeader={false}
    >
      {isBusy ? <FullPageProgress /> : null}
      {!isError ? (
        <EntityChrome
          entity={entity}
          layoutName={layoutName}
          showSidebar={showSidebar}
          showHeader={showHeader}
          showContainer={showContainer}
        />
      ) : (
        <ErrorMessage rtkQueryError={error} />
      )}
    </AppChrome>
  )
}
